import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY, type LocaleId, SUPPORTED_LOCALES } from './locale-id';
import type { TranslationDictionary } from './translation-dictionary';

/**
 * Loads and serves UI translations for English and French.
 * Persists the active locale in local storage so language choice survives reloads.
 */
@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly http = inject(HttpClient);
  private readonly dictionary = signal<TranslationDictionary>({});
  private readonly localeSignal = signal<LocaleId>(DEFAULT_LOCALE);

  /** Active locale code (`en` or `fr`). */
  readonly locale = this.localeSignal.asReadonly();

  /** Increments when translations change so pipes and templates can react. */
  readonly revision = computed(() => `${this.localeSignal()}:${this.dictionaryVersion()}`);

  private readonly dictionaryVersion = signal(0);

  /**
   * Loads the initial locale dictionary during application bootstrap.
   */
  initialize(): Promise<void> {
    const stored = this.readStoredLocale();
    const locale = stored ?? this.detectBrowserLocale();
    return this.setLocale(locale);
  }

  /**
   * Switches the active locale and reloads its translation dictionary.
   * @param locale Target locale (`en` or `fr`).
   */
  async setLocale(locale: LocaleId): Promise<void> {
    if (!SUPPORTED_LOCALES.includes(locale)) {
      return;
    }

    await this.loadDictionary(locale);
    this.localeSignal.set(locale);
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
    this.dictionaryVersion.update((value) => value + 1);
  }

  /**
   * Resolves a dot-notated translation key with optional `{{param}}` interpolation.
   * @param key Translation key (for example `home.heading`).
   * @param params Optional interpolation values.
   * @returns Translated string, or the key when no translation exists.
   */
  translate(key: string, params?: Record<string, string>): string {
    const template = resolveNestedKey(this.dictionary(), key);
    if (template === undefined) {
      return key;
    }

    return interpolate(template, params);
  }

  /**
   * Convenience helper for backend service display names under `services.<id>.label`.
   * @param serviceId Backend service identifier from configuration.
   */
  serviceLabel(serviceId: string): string {
    return this.translate(`services.${serviceId}.label`);
  }

  private async loadDictionary(locale: LocaleId): Promise<void> {
    const dictionary = await firstValueFrom(
      this.http.get<TranslationDictionary>(this.dictionaryUrl(locale)),
    );
    this.dictionary.set(dictionary);
  }

  /** Builds i18n asset URLs from the live document base href (for example `/`). */
  private dictionaryUrl(locale: LocaleId): string {
    const href = document.querySelector('base')?.getAttribute('href') ?? '/';
    const base = href.endsWith('/') ? href : `${href}/`;
    return `${base}assets/i18n/${locale}.json`;
  }

  private readStoredLocale(): LocaleId | null {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return stored === 'en' || stored === 'fr' ? stored : null;
  }

  private detectBrowserLocale(): LocaleId {
    const language = navigator.language.toLowerCase();
    return language.startsWith('fr') ? 'fr' : DEFAULT_LOCALE;
  }
}

function resolveNestedKey(dictionary: TranslationDictionary, key: string): string | undefined {
  const parts = key.split('.');
  let current: string | TranslationDictionary | undefined = dictionary;

  for (const part of parts) {
    if (current === undefined || typeof current === 'string') {
      return undefined;
    }

    current = current[part];
  }

  return typeof current === 'string' ? current : undefined;
}

function interpolate(template: string, params?: Record<string, string>): string {
  if (!params) {
    return template;
  }

  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => params[name] ?? `{{${name}}}`);
}
